import { getRegistration } from './auth';
import { isUnlockSessionReady } from './unlockSession';
import { cardsApi } from '../api/services/cardsApi';
import { CARD_STYLES } from '../constants/cardStyles';
import { CARD_TYPES, normalizeCardType } from '../constants/cardTypes';
import { MIN_DEPOSIT_TRANSACTION_USD } from '../constants/depositData';
import {
  addDecimalStrings,
  decimalGt,
  formatDecimal,
  parseDecimalInput,
  scaledIntToNormal,
  subtractDecimalStrings,
} from '../utils/decimalValues';

const STORAGE_KEY = 'obank_registration';
const FROZEN_KEY = 'obank_frozen_cards';
const SELECTED_KEY = 'obank_selected_card';
const STYLE_REVISION_KEY = 'obank_card_style_revision';
const FROZEN_REVISION_KEY = 'obank_frozen_revision';
const CARD_NAME_OVERRIDES_KEY = 'obank_card_name_overrides';
const CARD_STYLE_OVERRIDES_KEY = 'obank_card_style_overrides';
const CARD_UPDATED_EVENT = 'obank:card-updated';
const CARD_STATUS = {
  NOT_ISSUED: 'not_issued',
  ACTIVATING: 'activating',
  ACTIVE: 'active',
  FROZEN: 'frozen',
  BLOCKED: 'blocked',
};
const CARD_MONEY_DECIMALS = 2;
export const MAX_USER_CARDS = 5;
export const MIN_TOPUP_AMMOUNT = MIN_DEPOSIT_TRANSACTION_USD;

let cardsState = {
  cards: [],
  isLoaded: false,
  isLoading: false,
  error: null,
};

function emitCardUpdate() {
  window.dispatchEvent(new Event(CARD_UPDATED_EVENT));
}

function normalizeCardMoneyValue(value) {
  const raw = String(value ?? '').trim();

  if (!raw) return '0';

  if (!/^-?\d+(?:[.,]\d+)?$/.test(raw)) {
    return '0';
  }

  const normalized = scaledIntToNormal(raw, CARD_MONEY_DECIMALS, {
    fixed: false,
    trimZeros: true,
  });

  return parseDecimalInput(normalized) ? normalized : '0';
}

function formatCardMoneyValue(value) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return '$0.00';
  }

  return formatDecimal(decimal, 2, {
    prefix: '$',
    minFractionDigits: 2,
  });
}

function getCardMoneyValue(card, normalizedKey, rawKey, fallback = '0') {
  if (card?.[normalizedKey] !== undefined && card?.[normalizedKey] !== null) {
    const normalizedValue = String(card[normalizedKey] ?? '').trim();

    return parseDecimalInput(normalizedValue) ? normalizedValue : fallback;
  }

  if (card?.[rawKey] !== undefined && card?.[rawKey] !== null) {
    return normalizeCardMoneyValue(card[rawKey]);
  }

  return fallback;
}

export function isNotIssuedCardStatus(status) {
  return status === CARD_STATUS.NOT_ISSUED || status === 'pending';
}

export function isActivatingCardStatus(status) {
  return status === CARD_STATUS.ACTIVATING;
}

export function isFrozenCardStatus(status) {
  return status === CARD_STATUS.FROZEN;
}

export function isBlockedCardStatus(status) {
  return status === CARD_STATUS.BLOCKED;
}

export function isCardActionLocked(card) {
  return isActivatingCardStatus(card?.status) || isBlockedCardStatus(card?.status);
}

export function getCardIssueFundingProgress(card = {}) {
  const minBalanceToCreate = getCardMoneyValue(
    card,
    'minBalanceToCreate',
    'min_balance_to_create'
  );
  const balance = getCardMoneyValue(card, 'balanceValue', 'balance');
  const unconfirmedOperations = getCardMoneyValue(
    card,
    'unconfirmedOperations',
    'unconfirmed_operations_amount'
  );
  const cardPrice = getCardMoneyValue(card, 'cardPrice', 'card_price');
  const receivedAmount = addDecimalStrings(balance, unconfirmedOperations);
  const requiredAmount = addDecimalStrings(minBalanceToCreate, cardPrice);

  return {
    receivedAmount,
    requiredAmount,
    hasProgress: decimalGt(balance, '0') || decimalGt(unconfirmedOperations, '0'),
  };
}

export function calculateMinCardIssuePrice(card = {}) {
  const fundingProgress = getCardIssueFundingProgress(card);
  const requiredAmount = subtractDecimalStrings(
    fundingProgress.requiredAmount,
    fundingProgress.receivedAmount,
  );
  const requiredDecimal = parseDecimalInput(requiredAmount);

  if (!requiredDecimal || requiredDecimal.lte(0)) {
    return '0';
  }

  return requiredDecimal.toString();
}

export function calculateMinCardIssueTopUpAmount(card = {}) {
  const requiredAmount = calculateMinCardIssuePrice(card);
  const requiredDecimal = parseDecimalInput(requiredAmount);
  const minimumDecimal = parseDecimalInput(MIN_TOPUP_AMMOUNT);

  if (!requiredDecimal || requiredDecimal.lte(0)) {
    return '0';
  }

  return minimumDecimal && requiredDecimal.lt(minimumDecimal)
    ? minimumDecimal.toString()
    : requiredDecimal.toString();
}

export function isCardIssueFundingCovered(card = {}) {
  const requiredAmount = calculateMinCardIssuePrice(card);
  const requiredDecimal = parseDecimalInput(requiredAmount);

  return Boolean(requiredDecimal && requiredDecimal.eq(0));
}

export function formatCardMoneyAmount(value) {
  return formatCardMoneyValue(value);
}

export function normalizeCard(rawCard = {}) {
  const id = rawCard._id ?? rawCard.id;
  const staticData = rawCard.static_data ?? rawCard.staticData ?? {};
  const status = rawCard.status ?? CARD_STATUS.ACTIVE;
  const cardName = staticData.name ?? rawCard.card_name ?? rawCard.name ?? '';
  const fullName = rawCard.full_name ?? rawCard.fullName ?? rawCard.nameOnCard ?? '';
  const balanceValue = normalizeCardMoneyValue(rawCard.rawBalance ?? rawCard.balance);
  const minBalanceToCreate = normalizeCardMoneyValue(
    rawCard.min_balance_to_create ?? rawCard.minBalanceToCreate
  );
  const cardPrice = normalizeCardMoneyValue(rawCard.card_price ?? rawCard.cardPrice);
  const rawUnconfirmedOperations = rawCard.rawUnconfirmedOperations
    ?? rawCard.unconfirmed_operations_amount
    ?? rawCard.unconfirmedOperations;
  const unconfirmedOperations = normalizeCardMoneyValue(rawUnconfirmedOperations);
  const rawCryptoBalance = rawCard.rawCryptoBalance
    ?? rawCard.crypto_balance
    ?? rawCard.cryptoBalance;
  const cryptoBalance = normalizeCardMoneyValue(rawCryptoBalance);
  const uncreditedAmount = status === CARD_STATUS.ACTIVE
    ? normalizeCardMoneyValue(addDecimalStrings(
      rawUnconfirmedOperations ?? '0',
      rawCryptoBalance ?? '0',
    ))
    : '0';
  const hasProcessingFunds = status === CARD_STATUS.ACTIVE
    && decimalGt(unconfirmedOperations, '0');
  const processingAmount = hasProcessingFunds ? unconfirmedOperations : '0';
  const hasHeldFunds = status === CARD_STATUS.ACTIVE
    && decimalGt(cryptoBalance, '0');
  const heldAmount = hasHeldFunds ? cryptoBalance : '0';
  const hasUncreditedFunds = status === CARD_STATUS.ACTIVE
    && decimalGt(uncreditedAmount, '0');
  const issueFundingProgress = getCardIssueFundingProgress({
    minBalanceToCreate,
    balanceValue,
    unconfirmedOperations,
    cardPrice,
  });
  const rawAddress = rawCard.address;
  const address = rawAddress && typeof rawAddress === 'object'
    ? {
      country: String(rawAddress.country || '').trim(),
      city: String(rawAddress.city || '').trim(),
      address: String(rawAddress.address || '').trim(),
      postalCode: String(rawAddress.postal_code || rawAddress.postalCode || '').trim(),
    }
    : null;
  const normalizedAddress = address && Object.values(address).some(Boolean) ? address : null;

  return {
    ...rawCard,
    id,
    _id: id,
    type: normalizeCardType(rawCard.type ?? rawCard.card_type),
    status,
    style: staticData.style ?? rawCard.card_style ?? rawCard.style ?? 'sunset',
    name: cardName,
    label: cardName?.trim() || '',
    balance: formatCardMoneyValue(balanceValue),
    balanceValue,
    rawBalance: rawCard.balance,
    lastFour: rawCard.last4 ?? rawCard.lastFour ?? null,
    last4: rawCard.last4 ?? rawCard.lastFour ?? null,
    nameOnCard: fullName,
    fullName,
    address: normalizedAddress,
    minBalanceToCreate,
    cardPrice,
    unconfirmedOperations,
    rawUnconfirmedOperations,
    hasProcessingFunds,
    processingAmount,
    processingBalance: hasProcessingFunds ? formatCardMoneyValue(processingAmount) : '',
    cryptoBalance,
    rawCryptoBalance,
    hasHeldFunds,
    heldAmount,
    heldBalance: hasHeldFunds ? formatCardMoneyValue(heldAmount) : '',
    hasUncreditedFunds,
    uncreditedAmount,
    uncreditedBalance: hasUncreditedFunds ? formatCardMoneyValue(uncreditedAmount) : '',
    issueFundingProgress: {
      ...issueFundingProgress,
      receivedLabel: formatCardMoneyValue(issueFundingProgress.receivedAmount),
      requiredLabel: formatCardMoneyValue(issueFundingProgress.requiredAmount),
    },
    issueFundingCovered: isCardIssueFundingCovered({
      minBalanceToCreate,
      balanceValue,
      unconfirmedOperations,
      cardPrice,
    }),
    addressTrc20: rawCard.address_trc20 ?? rawCard.addressTrc20 ?? null,
    staticData,
    secretData: rawCard.card_number
      ? {
        pan: rawCard.card_number,
        cvv: rawCard.cvv,
        expiry: rawCard.expiry_date,
        staticPassword: rawCard.static_password,
        pinCode: rawCard.card_pin_code,
      }
      : null,
  };
}

function normalizeCardsResponse(response) {
  const data = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : [];

  return data.map(normalizeCard).filter((card) => card.id);
}

function replaceCard(nextCard) {
  const normalizedCard = normalizeCard(nextCard);
  const index = cardsState.cards.findIndex((card) => card.id === normalizedCard.id);
  const cards = index >= 0
    ? cardsState.cards.map((card) => (card.id === normalizedCard.id ? normalizedCard : card))
    : [normalizedCard, ...cardsState.cards];

  cardsState = {
    ...cardsState,
    cards,
    isLoaded: true,
    error: null,
  };
  emitCardUpdate();

  return normalizedCard;
}

function buildCardDetailsPatch(card, setData = {}, unsetData = {}) {
  const nextStaticData = {
    ...(card?.staticData || {}),
  };
  const nextCard = {
    ...(card || {}),
    static_data: {
      ...(card?.static_data || {}),
    },
    staticData: nextStaticData,
  };

  if (
    Object.prototype.hasOwnProperty.call(setData, 'name')
    || Object.prototype.hasOwnProperty.call(setData, 'card_name')
  ) {
    const nextName = Object.prototype.hasOwnProperty.call(setData, 'name')
      ? setData.name
      : setData.card_name;

    nextStaticData.name = nextName;
    nextCard.static_data.name = nextName;
    nextCard.card_name = nextName;
    nextCard.name = nextName;
  }

  if (
    Object.prototype.hasOwnProperty.call(setData, 'style')
    || Object.prototype.hasOwnProperty.call(setData, 'card_style')
  ) {
    const nextStyle = Object.prototype.hasOwnProperty.call(setData, 'style')
      ? setData.style
      : setData.card_style;

    nextStaticData.style = nextStyle;
    nextCard.static_data.style = nextStyle;
    nextCard.card_style = nextStyle;
    nextCard.style = nextStyle;
  }

  if (unsetData.card_name === true || unsetData.name === true) {
    delete nextStaticData.name;
    delete nextCard.static_data.name;
    delete nextCard.card_name;
    delete nextCard.name;
  }

  if (unsetData.card_style === true || unsetData.style === true) {
    delete nextStaticData.style;
    delete nextCard.static_data.style;
    delete nextCard.card_style;
    delete nextCard.style;
  }

  return nextCard;
}

export function getCardsSnapshot() {
  return cardsState;
}

export function getCards() {
  return cardsState.cards;
}

export async function refreshCards(options = {}) {
  const { limit = 25, silent = false, requireUnlockSession = true } = options;

  if (requireUnlockSession && !isUnlockSessionReady()) {
    return cardsState.cards;
  }

  if (!silent) {
    cardsState = {
      ...cardsState,
      isLoading: true,
      error: null,
    };
    emitCardUpdate();
  }

  try {
    const response = await cardsApi.getAll({ limit });
    const cards = normalizeCardsResponse(response);

    cardsState = {
      cards,
      isLoaded: true,
      isLoading: false,
      error: null,
    };
    emitCardUpdate();

    return cards;
  } catch (error) {
    if (!silent) {
      cardsState = {
        ...cardsState,
        isLoading: false,
        error,
      };
      emitCardUpdate();
    }
    throw error;
  }
}

export async function refreshCardById(id) {
  const nextCard = await cardsApi.getById({ id });

  return replaceCard(nextCard);
}

export async function revealCardSecretData(id) {
  const nextCard = await cardsApi.revealSecretData({ id });

  return replaceCard(nextCard);
}

export async function createRemoteCard({
  type = CARD_TYPES.VIRTUAL,
  style = 'sunset',
  name = '',
  nameOnCard = '',
} = {}) {
  const trimmedName = name.trim();
  const data = {
    card_style: style,
    full_name: nameOnCard.trim(),
    card_type: normalizeCardType(type),
  };

  if (trimmedName) {
    data.card_name = trimmedName;
  }

  const nextCard = await cardsApi.create({ data });
  const normalizedCard = replaceCard(nextCard);

  setSelectedCardId(normalizedCard.id);

  return normalizedCard;
}

export async function getRemoteCardDepositAddress(id, fundingAsset) {
  const address = await cardsApi.getDepositAddress({
    id,
    currencyId: fundingAsset?.currencyId,
    network: fundingAsset?.networkId || fundingAsset?.network,
  });
  const currentCard = cardsState.cards.find((card) => card.id === id);

  if (currentCard) {
    replaceCard({
      ...currentCard,
      depositAddress: address,
      issueTopUp: {
        ...currentCard.issueTopUp,
        address: address?.address,
        addressId: address?._id,
        memo: address?.memo,
        asset: fundingAsset?.asset,
        network: fundingAsset?.network,
        networkId: fundingAsset?.networkId,
        currencyId: fundingAsset?.currencyId,
        updatedAt: Date.now(),
      },
    });
  }

  return address;
}

export async function updateRemoteCardStatus(id, status) {
  await cardsApi.updateStatus({ id, status });

  const currentCard = cardsState.cards.find((card) => card.id === id);
  if (currentCard) {
    return replaceCard({ ...currentCard, status });
  }

  return refreshCardById(id);
}

export async function updateRemoteCardDetails(id, setData = {}, unsetData = {}) {
  const nextCard = await cardsApi.updateDetails({
    id,
    data: {
      _id: id,
      set_data: setData,
      unset_data: unsetData,
    },
  });
  const currentCard = cardsState.cards.find((card) => card.id === id);
  const responseCard = nextCard && (nextCard._id || nextCard.id)
    ? nextCard
    : currentCard || { _id: id, id };

  return replaceCard(buildCardDetailsPatch(responseCard, setData, unsetData));
}

export function updateRemoteCardPinCode(id, pinCode) {
  return cardsApi.updatePinCode({ id, pinCode });
}

function saveRegistration(updated) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
}

function readFrozenIds() {
  try {
    return JSON.parse(sessionStorage.getItem(FROZEN_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function writeFrozenIds(ids) {
  sessionStorage.setItem(FROZEN_KEY, JSON.stringify(ids));
}

function readCardNameOverrides() {
  try {
    return JSON.parse(sessionStorage.getItem(CARD_NAME_OVERRIDES_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeCardNameOverrides(overrides) {
  sessionStorage.setItem(CARD_NAME_OVERRIDES_KEY, JSON.stringify(overrides));
}

function readCardStyleOverrides() {
  try {
    return JSON.parse(sessionStorage.getItem(CARD_STYLE_OVERRIDES_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeCardStyleOverrides(overrides) {
  sessionStorage.setItem(CARD_STYLE_OVERRIDES_KEY, JSON.stringify(overrides));
}

export function hasCard() {
  return cardsState.cards.length > 0;
}

export function getIssuedCardsCount(currentUser) {
  const value = currentUser?.stats?.issued_cards ?? currentUser?.stats?.issuedCards;

  if (value === undefined || value === null || value === '') {
    return null;
  }

  const count = Number(value);
  return Number.isFinite(count) ? count : null;
}

export function getUserCardCount(cards = cardsState.cards, currentUser) {
  const loadedCardsCount = Array.isArray(cards) ? cards.length : 0;
  const issuedCardsCount = getIssuedCardsCount(currentUser);

  return issuedCardsCount === null
    ? loadedCardsCount
    : Math.max(loadedCardsCount, issuedCardsCount);
}

export function isCardLimitReached(cards = cardsState.cards, currentUser) {
  return getUserCardCount(cards, currentUser) >= MAX_USER_CARDS;
}

export function getCard() {
  return cardsState.cards[0] ?? null;
}

export function getCardById(id) {
  if (!id) return null;

  return cardsState.cards.find((card) => card.id === id || card._id === id) ?? null;
}

export function createCard({
  type = CARD_TYPES.VIRTUAL,
  style = 'sunset',
  name = '',
  nameOnCard = '',
  status = 'active',
} = {}) {
  const registration = getRegistration() ?? {};
  const card = {
    type: normalizeCardType(type),
    style,
    name: name.trim(),
    nameOnCard: nameOnCard.trim(),
    status,
    lastFour: status === 'pending' ? null : String(Math.floor(1000 + Math.random() * 9000)),
    createdAt: Date.now(),
  };
  saveRegistration({ ...registration, card });
  setSelectedCardId('user');
  bumpCardRevision();
  return card;
}

export function getPostUnlockPath(currentUser) {
  const issuedCardsCount = getIssuedCardsCount(currentUser);

  if (issuedCardsCount !== null) {
    return issuedCardsCount > 0 ? '/home' : '/cards/new';
  }

  return hasCard() ? '/home' : '/cards/new';
}

export function getSelectedCardId() {
  const stored = sessionStorage.getItem(SELECTED_KEY);
  if (stored) {
    return stored;
  }
  return cardsState.cards[0]?.id ?? '';
}

export function setSelectedCardId(id) {
  sessionStorage.setItem(SELECTED_KEY, id);
}

export function isCardFrozen(cardId) {
  const card = cardsState.cards.find((item) => item.id === cardId);

  return isFrozenCardStatus(card?.status) || readFrozenIds().includes(cardId);
}

export function freezeCard(cardId) {
  const ids = readFrozenIds();
  if (!ids.includes(cardId)) {
    writeFrozenIds([...ids, cardId]);
    bumpFrozenRevision();
  }
}

export function unfreezeCard(cardId) {
  const ids = readFrozenIds();
  if (ids.includes(cardId)) {
    writeFrozenIds(ids.filter((id) => id !== cardId));
    bumpFrozenRevision();
  }
}

export function getFrozenCardIds() {
  return readFrozenIds();
}

export function getCardStyleRevision() {
  return Number(sessionStorage.getItem(STYLE_REVISION_KEY) ?? 0);
}

export function getFrozenRevision() {
  return Number(sessionStorage.getItem(FROZEN_REVISION_KEY) ?? 0);
}

export function getCardNameOverrides() {
  return readCardNameOverrides();
}

export function getCardStyleOverrides() {
  return readCardStyleOverrides();
}

export function subscribeCardUpdates(onStoreChange) {
  window.addEventListener(CARD_UPDATED_EVENT, onStoreChange);
  return () => window.removeEventListener(CARD_UPDATED_EVENT, onStoreChange);
}

function bumpCardRevision() {
  sessionStorage.setItem(STYLE_REVISION_KEY, String(getCardStyleRevision() + 1));
  emitCardUpdate();
}

function bumpFrozenRevision() {
  sessionStorage.setItem(FROZEN_REVISION_KEY, String(getFrozenRevision() + 1));
  emitCardUpdate();
}

export function updateCardStyle(style, cardId = 'user') {
  if (!CARD_STYLES.includes(style)) return null;

  if (cardId && cardId !== 'user') {
    const overrides = readCardStyleOverrides();
    writeCardStyleOverrides({ ...overrides, [cardId]: style });
    bumpCardRevision();
    return { id: cardId, style };
  }

  const registration = getRegistration();
  if (!registration?.card) return null;

  const card = { ...registration.card, style };
  saveRegistration({ ...registration, card });
  bumpCardRevision();
  return card;
}

export function updateCardName(cardId, name) {
  const nextName = name.trim();
  if (!cardId || !nextName) return null;

  if (cardId === 'user') {
    const registration = getRegistration();
    if (!registration?.card) return null;

    const card = { ...registration.card, name: nextName };
    saveRegistration({ ...registration, card });
    bumpCardRevision();
    return card;
  }

  const overrides = readCardNameOverrides();
  writeCardNameOverrides({ ...overrides, [cardId]: nextName });
  bumpCardRevision();
  return { id: cardId, name: nextName };
}

export function updateCardIssueTopUp(issueTopUp) {
  const registration = getRegistration();
  if (!registration?.card) return null;

  const card = {
    ...registration.card,
    status: 'pending',
    issueTopUp: {
      ...registration.card.issueTopUp,
      ...issueTopUp,
      updatedAt: Date.now(),
    },
  };

  saveRegistration({ ...registration, card });
  bumpCardRevision();
  return card;
}

export function activatePendingCard() {
  const registration = getRegistration();
  if (!registration?.card) return null;

  const card = {
    ...registration.card,
    status: 'active',
    lastFour: registration.card.lastFour || String(Math.floor(1000 + Math.random() * 9000)),
    issueTopUp: null,
  };

  saveRegistration({ ...registration, card });
  bumpCardRevision();
  return card;
}

export function deletePendingCard() {
  const registration = getRegistration();
  if (!registration?.card || registration.card.status !== 'pending') return null;

  const { card: _card, ...restRegistration } = registration;
  saveRegistration(restRegistration);
  setSelectedCardId('main');
  bumpCardRevision();
  return true;
}
